import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Building2, Users, LogOut, User, Mail, AtSign, Check } from 'lucide-react';

export function UserProfilePage() {
  const navigate = useNavigate();
  const {
    user,
    activeOrganization,
    activeTeam,
    setActiveOrganization,
    setActiveTeam,
    logout,
    isLoading,
  } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    navigate('/login');
    return null;
  }

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'owner':
        return 'default';
      case 'admin':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  return (
    <div className="container max-w-4xl mx-auto py-8 px-4">
      <h1 className="text-2xl font-bold mb-6">Profile</h1>

      <div className="space-y-6">
        {/* Personal Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Personal Information
            </CardTitle>
            <CardDescription>Your account details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-muted-foreground mb-1">Display Name</div>
                <div className="font-medium">{user.display_name || user.username}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1 flex items-center gap-1">
                  <AtSign className="h-3 w-3" />
                  Username
                </div>
                <div className="font-medium">{user.username}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1 flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  Email
                </div>
                <div className="font-medium">{user.email || '-'}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">User ID</div>
                <div className="font-mono text-sm">{user.id}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Organizations */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Organizations
            </CardTitle>
            <CardDescription>Organizations you belong to</CardDescription>
          </CardHeader>
          <CardContent>
            {user.organizations && user.organizations.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Organization</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="w-[100px]">Active</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {user.organizations.map((org) => (
                    <TableRow
                      key={org.id}
                      className={`cursor-pointer hover:bg-muted/50 ${
                        activeOrganization?.id === org.id ? 'bg-muted/30' : ''
                      }`}
                      onClick={() => setActiveOrganization(org.id)}
                    >
                      <TableCell className="font-medium">{org.name}</TableCell>
                      <TableCell>
                        <Badge variant={getRoleBadgeVariant(org.role)}>
                          {org.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {activeOrganization?.id === org.id && (
                          <Check className="h-4 w-4 text-green-600" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-sm text-muted-foreground py-4 text-center">
                No organizations found
              </div>
            )}
          </CardContent>
        </Card>

        {/* Teams */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Teams
              {activeOrganization && (
                <span className="text-sm font-normal text-muted-foreground">
                  ({activeOrganization.name})
                </span>
              )}
            </CardTitle>
            <CardDescription>
              {activeOrganization
                ? `Teams in ${activeOrganization.name}`
                : 'All your teams'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {user.teams && user.teams.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Team</TableHead>
                    <TableHead>Organization</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="w-[100px]">Active</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {user.teams
                    .filter((team) =>
                      activeOrganization
                        ? team.organization_id === activeOrganization.id
                        : true
                    )
                    .map((team) => {
                      const org = user.organizations?.find(
                        (o) => o.id === team.organization_id
                      );
                      return (
                        <TableRow
                          key={team.id}
                          className={`cursor-pointer hover:bg-muted/50 ${
                            activeTeam?.id === team.id ? 'bg-muted/30' : ''
                          }`}
                          onClick={() => setActiveTeam(team.id)}
                        >
                          <TableCell className="font-medium">{team.name}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {org?.name || '-'}
                          </TableCell>
                          <TableCell>
                            <Badge variant={getRoleBadgeVariant(team.role)}>
                              {team.role}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {activeTeam?.id === team.id && (
                              <Check className="h-4 w-4 text-green-600" />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            ) : (
              <div className="text-sm text-muted-foreground py-4 text-center">
                No teams found
              </div>
            )}
          </CardContent>
        </Card>

        {/* Account Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Account</CardTitle>
            <CardDescription>Manage your account</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="destructive" onClick={logout} className="flex items-center gap-2">
              <LogOut className="h-4 w-4" />
              Sign Out
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default UserProfilePage;
